import { useState, useEffect } from "react"
import { Table, Input, Button, Space, Card, Form, Modal, message } from "antd"
import { SearchOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import FormInput from "../../components/common/FormInput/FormInput"
import rollbackIcon from "../../assets/icons/rollback.png";
import axios from "axios"
import {
  addSelectedEntity,
  removeSelectedEntity,
  clearSelectedEntities
} from "../../redux/slices/entitySlice"
import { useDispatch, useSelector } from "react-redux"

// Page descriptions mapping
const pageDescriptions = {
  "SUPPLIER_DASHBOARD": "Main dashboard for suppliers to manage their profile and activities",
  "ADMIN_DASHBOARD": "Administrative dashboard for system management and oversight",
  "REPORTING": "Analytics and reporting tools for generating insights and reports",
  "SUPPLIER_SEARCH": "Search interface for finding and filtering suppliers in the system",
  "GOALS": "Goal setting and tracking functionality for performance management"
}

const EntityManagement = () => {
  const [entities, setEntities] = useState([])
  const [selectedPages, setSelectedPages] = useState([])
  const [searchText, setSearchText] = useState("")
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingEntity, setEditingEntity] = useState(null)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [pages, setPages] = useState([])
  const dispatch = useDispatch()
  
  const selectedEntities = useSelector(state => state.entity.selectedEntities)
  const page = ["SUPPLIER_DASHBOARD", "ADMIN_DASHBOARD", "REPORTING", "SUPPLIER_SEARCH", "GOALS"]
  
  useEffect(() => {
    if (entities.length > 0) {
      localStorage.setItem("entities", JSON.stringify(entities))
    }
  }, [entities])

  const filteredEntities = entities.filter(
    (entity) =>
      entity.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      entity.description?.toLowerCase().includes(searchText.toLowerCase()),
  )

  const showEditModal = (record) => {
    setEditingEntity(record)
    form.setFieldsValue({
      entityName: record.name,
      entityDescription: record.description,
    })
    setIsModalVisible(true)
  }

  const handleSave = () => {
    setEntities([...selectedPages])
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleDelete = (record) => {
    const pageToDelete = record.name;
    setEntities(prevPages => prevPages.filter(page => page !== pageToDelete));
    setSelectedPages(prevPages => prevPages.filter(page => page !== pageToDelete));
  }
  

  const handleClear = () => {
    setSearchText("")
  
    setLoading(true)
    setTimeout(() => {
      setEntities([]) 
      
      const roles = ["Super Admin", "Admin", "Normal Admin", "Supplier Admin", "Supplier User"]
      roles.forEach(role => {
        localStorage.removeItem(`permissions_${role}`)
      })
      
      localStorage.removeItem("entities")
      
      setLoading(false)
    }, 500)
  }

  function handleSelect(value) {
    setSelectedPages(value)
  }

  function handleEntitySave() {
    
    dispatch(clearSelectedEntities())
    
    
    dispatch(addSelectedEntity(selectedPages))
    
    
    const currentSelectedPages = [...selectedPages]
    
    
    if(currentSelectedPages.length > 0) {
      navigate("/admin/roles-permissions/permissions")
    } else {
      message.error("Please select at least one entity before saving")
    }
  }
  const handleBackClick = () => {
  
    navigate(`/admin`);
  };


  return (
    <div className="min-h-screen bg-white p-4 md:p-8">
     <div className="flex justify-end mb-4">
        <Button
          shape="round"
          icon={<img src={rollbackIcon || "/placeholder.svg"} alt="back" className="w-4 h-4 mr-1" />}
          size="small"
          className="bg-[#F8FAFC] h-8"
          style={{ border: "1px solid #A1BAD1" }}
          onClick={handleBackClick}
        >
          Back
        </Button>
      </div>
      <div className="mx-auto max-w-7xl space-y-8">
  
        <Card className="border border-gray-300 rounded-md shadow-none">
          <h1 className="text-2xl font-bold mb-6 text-[#2c5477]">Select Entites</h1>
          <Form form={form} layout="vertical">
            <div className="grid grid-cols-1 gap-4">
            <FormInput 
              options={page}
              placeholder={"Entity"}
              label={"Entity"}
              mode={"multiple"}
              onChange={(value) => handleSelect(value)}
              formFieldType={"select"}
            />
            </div>
            <div className="flex justify-center mt-4">
              <Button
                type="primary"
                onClick={(values) => handleSave(values)} 
                className="bg-[#2c5477] hover:bg-[#1e3a54] border-none px-8"
              >
                Add
              </Button>
            </div>
          </Form>
        </Card>

        <Card className="border border-gray-300 rounded-md shadow-none">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-[#2c5477]">Entities</h1>
            
          </div>

          <Table
            columns={[
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
                align: "center", 
              },
              {
                title: "Description",
                dataIndex: "description",
                key: "description",
                align: "center",
              },
              {
                title: "Actions",
                key: "actions",
                align: "center",
                render: (_, record) => (
                  <Space size="middle">
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(record)}
                      className="border-none shadow-none"
                    />
                  </Space>
                ),
              },
            ]}
            dataSource={entities?.map((page, index) => ({
              key: index,      
              name: page,     
              description: pageDescriptions[page] || "",  
            }))}
            loading={loading}
            pagination={false}
            className="mb-6"
            style={{
              "--header-bg": "#2c5477",
              "--header-color": "white",
            }}
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      backgroundColor: "#2c5477",
                      color: "white",
                      padding: "16px",
                      textAlign: "center", 
                    }}
                  />
                ),
              },
            }}
          />

          <div className="flex justify-end mt-4 space-x-4">
            <Button 
              type="primary" 
              onClick={handleEntitySave} 
              className="bg-[#2c5477] hover:bg-[#1e3a54] border-none px-8"
            >
              Save
            </Button>
            <Button 
              onClick={handleClear} 
              className="bg-[#2c5477] text-white hover:bg-[#1e3a54] border-none px-8"
            >
              Clear
            </Button>
          </div>
        </Card>
      </div>

      <Modal
        title="Edit Entity"
        open={isModalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        okText="Save"
        okButtonProps={{ className: "bg-[#2c5477] hover:bg-[#1e3a54] border-none" }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="entityName"
            label="Entity Name"
            rules={[{ required: true, message: "Please enter entity name" }]}
          >
            <Input placeholder="Enter entity name" />
          </Form.Item>
          <Form.Item 
            name="entityDescription" 
            label="Entity Description"
          >
            <Input placeholder="Enter entity description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default EntityManagement